import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { InLineControlTableRowViewModel } from 'src/app/models/viewModels/inLineControlTableRowViewModel';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { Evidence } from 'src/app/models/api/finding.model';
import { StepType } from 'src/app/models/api/agentStep.model';
import { formatFromDateWithSeconds } from 'src/app/common/datetime_utility';
import { AiService } from 'src/app/models/api/agentStep.model';
import { getAiServiceDisplayName, getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { isTaintAnalysisRule } from 'src/app/common/findingRulesUtils';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { Observable, filter, take, map } from 'rxjs';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { InLineActivityStepTableRowViewModel } from 'src/app/models/viewModels/InLineActivityStepTableRowView.model';
import { SearchActivityHttpService } from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';
import { InLineControlModel } from 'src/app/models/api/inLineControl.model';

@Component({
  selector: 'app-enterprise-ai-in-line-control-details',
  templateUrl: './enterprise-ai-in-line-control-details.component.html',
  styleUrl: './enterprise-ai-in-line-control-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnterpriseAiInLineControlDetailsComponent implements OnInit {
  public inLineControl = input<string>();
  public integration = input<string>();
  public viewModel = input<InLineControlTableRowViewModel>();

  private readonly _fetchedModel = signal<InLineControlTableRowViewModel | undefined>(undefined);
  public isLoading = signal<boolean>(false);

  public model: Signal<InLineControlTableRowViewModel | undefined> = computed(() => {
    return this.viewModel() ?? this._fetchedModel();
  });

  public isTaintAnalysis: Signal<boolean> = computed(() => {
    const model = this.model();
    return isTaintAnalysisRule(model?.ruleName);
  });

  public statusLabel: Signal<StatusLabelEnum> = computed(() => {
    const model = this.model();
    if (!model || !model.preventionType) return StatusLabelEnum.open;

    switch (model.preventionType.toLowerCase()) {
      case 'blocked':
        return StatusLabelEnum.blocked;
      case 'detected':
        return StatusLabelEnum.detected;
      default:
        return StatusLabelEnum.open;
    }
  });

  public showNoData = false;
  public tabs: ISidepanelTab[] = [{ labelPath: 'inventoryResourcePage.overviewTabTitle' }];
  public activeTabIndex = 0;
  public summaryCardFields: Signal<ICardField[]> = computed(() => {
    const model = this.model();
    if (!model) return [];
    return this.isTaintAnalysis()
      ? this.getTaintAnalysisSummaryFields(model)
      : this.getNonTaintAnalysisSummaryFields(model);
  });

  public actorCardFields: Signal<ICardField[]> = computed(() => {
    const model = this.model();
    if (!model) return [];
    return this.getActorFields(model);
  });

  private readonly _labelsProviderPipe = inject(LabelsProviderPipe);
  private readonly _logger = inject(LoggerService);
  private readonly _router = inject(Router);
  private readonly _beautifyDisplayStringPipe = inject(BeautifyDisplayStringPipe);
  private readonly _sidePanelService = inject(SidePanelService);
  private readonly _searchActivityHttpService = inject(SearchActivityHttpService);
  private readonly _destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    if (this.viewModel()) {
      return;
    }

    const findingId = this.inLineControl();
    if (!findingId) {
      return;
    }

    this.isLoading.set(true);
    this._searchActivityHttpService
      .getInLineControlFindingById(findingId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (finding) => {
          if (finding) {
            this._fetchedModel.set(this._transformToViewModel(finding));
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          this.isLoading.set(false);
          this._logger.warn('Failed to fetch in-line control finding', { findingId, error });
        },
      });
  }

  public onTabSelect(index: number): void {
    this.activeTabIndex = index;
  }

  public onViewRelatedFindingsClick(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        this._sidePanelService.close();
      });
  }

  public onStepLinkClick(evidence: Evidence): void {
    if (!this.hasStepId(evidence)) {
      return;
    }
    const model = this.model();
    if (!model) {
      return;
    }

    const stepResource = {
      id: evidence.stepId,
      integrationId: model.integrationId,
      threadId: model.threadId,
    };

    this._sidePanelService.openInLineStepDetails(stepResource as unknown as InLineActivityStepTableRowViewModel, {
      inline: true,
      stack: true,
    });
  }

  private _transformToViewModel(row: InLineControlModel): InLineControlTableRowViewModel {
    const aiService = this._resolveAiService(row);
    return {
      rowId: row?.id ?? '',
      id: row?.id ?? '',
      integrationId: row?.integrationId ?? '',
      ruleName: row?.rule?.name ?? '',
      ruleId: row?.rule?.id ?? '',
      preventionType: row?.rule?.action === 'block' ? 'Blocked' : 'Detected',
      preventionTypeIcon:
        row?.rule?.action === 'block'
          ? 'assets/images/dataflow-icons/blocked.svg'
          : 'assets/images/dataflow-icons/shield-alert.svg',
      timestamp: row?.timestamp ? formatFromDateWithSeconds(new Date(row.timestamp)) : '',
      agent: row?.relatedSteps?.[0]?.context?.agent?.name?.trim() || '',
      actor: row?.actor?.displayName || row?.mainActor?.id || undefined,
      threadId: row?.threadId ?? '',
      agentId: row?.relatedSteps?.[0]?.context?.agent?.id || undefined,
      clickable: false,
      aiServiceIcon: getAiServiceLogoPath(aiService),
      aiService: aiService,
      ruleSummary: row?.rule?.summary ?? '',
      taintAnalysisEvidence: row?.evidence,
      findingsEvidence: row?.evidence,
      environmentId: this._extractEnvironmentId(row),
    };
  }

  private _extractEnvironmentId(row: InLineControlModel): string {
    type ContextWithProps = {
      additionalProperties?: { environmentId?: string };
    };
    const context = row?.relatedSteps?.[0]?.context;
    return (context as ContextWithProps)?.additionalProperties?.environmentId ?? '';
  }

  private _resolveAiService(row: InLineControlModel): AiService {
    if (row.aiService) {
      return row.aiService;
    }

    let app = (row.relatedSteps?.[0]?.context?.agent?.provider?.name || '').toLowerCase();
    app = app.indexOf('github') === -1 ? app : 'github';

    switch (app) {
      case 'custom agent':
        return AiService.customAgent;
      case 'github':
        return AiService.gitHub;
      case 'slack':
        return AiService.slack;
      case 'copilot app':
      case 'm365copilot':
      case 'cps':
      case 'copilot':
        return AiService.m365Copilot;
      case 'azure foundry':
        return AiService.azureAiFoundry;
      case 'chatgpt':
        return AiService.chatGptEnterprise;
      case 'claude desktop':
        return AiService.claude;
      case 'copilot studio':
        return AiService.copilotStudio;
      case 'perplexity':
        return AiService.perplexity;
      case 'agentkit':
        return AiService.agentKit;
      case 'cursor':
        return AiService.cursor;
      default:
        return AiService.m365Copilot;
    }
  }

  private _safeTransformLabel(labelKey: string): string {
    try {
      return this._labelsProviderPipe.transform(labelKey);
    } catch (error) {
      this._logger.warn('Error transforming label', { labelKey, error });
      return labelKey;
    }
  }

  private getTaintAnalysisSummaryFields(model: InLineControlTableRowViewModel): ICardField[] {
    try {
      if (!model) return [];

      const fields: ICardField[] = [
        {
          key: this._safeTransformLabel('aiInLineControlPage.ruleIdLabel'),
          value: model.ruleId || '',
          copyableText: model.ruleId || '',
          isCopyable: true,
        },
        {
          key: this._safeTransformLabel('aiInLineControlPage.threadIdLabel'),
          value: model.threadId || '',
          copyableText: model.threadId || '',
          isCopyable: true,
        },
      ];

      if (model.agentId) {
        fields.push({
          key: this._safeTransformLabel('aiInLineControlPage.agentIdLabel'),
          value: '',
          copyableText: model.agentId,
          isCopyable: true,
          hideValue: true,
        });
      }

      fields.push({
        key: this._safeTransformLabel('aiInLineControlPage.aiServiceLabel'),
        value: model.aiService ? getAiServiceDisplayName(model.aiService) : 'N/A',
        iconPath: model.aiServiceIcon || '',
      });

      const agentNameAvailable = model.agent && model.agent.trim() !== '' && model.agent !== 'N/A';

      if (agentNameAvailable) {
        const agentField: ICardField = {
          key: this._safeTransformLabel('aiInLineControlPage.agentNameLabel'),
          value: model.agent,
          iconPath: model.aiServiceIcon || '',
        };

        if (model.aiService === AiService.copilotStudio && model.agentId) {
          agentField.method = () => {
            const queryParam = `search:"${model.agentId}" category:bot`;
            this._router.navigate(['app/inventory'], {
              queryParams: { q: queryParam },
            });
          };
        }

        fields.push(agentField);
      } else if (model.agentId) {
        fields.push({
          key: this._safeTransformLabel('aiInLineControlPage.agentNameLabel'),
          value: model.agentId,
          copyableText: model.agentId,
          isCopyable: true,
        });
      }

      fields.push({
        key: this._safeTransformLabel('aiInLineControlPage.preventionTypeLabel'),
        value: model.preventionType || '',
        iconPath: model.preventionTypeIcon || '',
      });

      return fields;
    } catch (error) {
      this._logger.error('Error in getTaintAnalysisSummaryFields', { model }, error as Error);
      return [];
    }
  }

  private getNonTaintAnalysisSummaryFields(model: InLineControlTableRowViewModel): ICardField[] {
    try {
      if (!model) return [];

      const fields: ICardField[] = [
        {
          key: this._safeTransformLabel('aiFindingPage.ruleIdLabel'),
          value: model.ruleId || '',
          copyableText: model.ruleId || '',
          isCopyable: true,
        },
        {
          key: this._safeTransformLabel('aiFindingPage.threadIdLabel'),
          value: model.threadId || '',
          copyableText: model.threadId || '',
          isCopyable: true,
        },
        {
          key: this._safeTransformLabel('aiFindingPage.aiServiceLabel'),
          value: model.aiService ? getAiServiceDisplayName(model.aiService) : 'N/A',
          iconPath: model.aiServiceIcon || '',
        },
      ];

      const agentNameAvailable = model.agent && model.agent.trim() !== '' && model.agent !== 'N/A';

      if (agentNameAvailable) {
        const agentField: ICardField = {
          key: this._safeTransformLabel('aiFindingPage.agentNameLabel'),
          value: model.agent,
          iconPath: model.aiServiceIcon || '',
        };

        if (model.aiService === AiService.copilotStudio && model.agentId) {
          agentField.method = () => {
            const queryParam = `search:"${model.agentId}" category:bot`;
            this._router.navigate(['app/inventory'], {
              queryParams: { q: queryParam },
            });
          };
        }

        fields.push(agentField);
      } else if (model.agentId) {
        fields.push({
          key: this._safeTransformLabel('aiFindingPage.agentNameLabel'),
          value: model.agentId,
          copyableText: model.agentId,
          isCopyable: true,
        });
      }

      fields.push({
        key: this._safeTransformLabel('aiAgentStepPage.environmentIdFieldName'),
        value: model.environmentId || 'N/A',
        copyableText: model.environmentId || '',
        isCopyable: !!model.environmentId,
      });

      return fields;
    } catch (error) {
      this._logger.error('Error in getNonTaintAnalysisSummaryFields', { model }, error as Error);
      return [];
    }
  }

  private getActorFields(model: InLineControlTableRowViewModel): ICardField[] {
    try {
      if (!model) return [];

      return [
        {
          key: this._safeTransformLabel('aiFindingPage.nameLabel'),
          value: model.actor || 'N/A',
          isCopyable: true,
        },
        {
          key: this._safeTransformLabel('aiFindingPage.emailLabel'),
          value: 'N/A',
          isCopyable: true,
        },
      ];
    } catch (error) {
      this._logger.error('Error in getActorFields', { model }, error as Error);
      return [];
    }
  }

  public getStepType(evidence: Evidence): StepType | undefined {
    const model = this.model();
    if (!model?.findingsEvidence) return undefined;

    // For In-Line Control, we need to simulate step details since we don't have full AgentStepModel
    // We'll use a default step type based on the evidence location
    if (evidence.location?.includes('message') || evidence.location?.includes('input')) {
      return StepType.userMessageStep;
    }
    if (evidence.location?.includes('ai') || evidence.location?.includes('response')) {
      return StepType.aiMessageStep;
    }
    // Default to userMessageStep for most cases
    return StepType.userMessageStep;
  }

  public getLocation(evidence: Evidence): string {
    let result = '';
    if (!evidence.location) {
      return result;
    }

    const splitLocation = evidence.location.split('.');
    if (splitLocation.length > 0) {
      const formattedString = this._beautifyDisplayStringPipe.transform(splitLocation[splitLocation.length - 1]);
      result += ` > ${formattedString}`;
    }
    return result;
  }

  public hasStepId(evidence: Evidence): boolean {
    const stepId = evidence?.stepId?.trim() || '';
    return stepId !== '';
  }

  public hasCoreEvidence(evidence: Evidence): boolean {
    const coreEvidence = evidence?.coreEvidence?.trim() || '';
    return coreEvidence !== '';
  }

  public canShowEvidenceContent(evidence: Evidence): boolean {
    return this.hasStepId(evidence) && this.hasCoreEvidence(evidence);
  }

  public getSensitiveDataMethod(
    evidence: Evidence
  ): () => Observable<{ transcript?: string; resultCode: string; title: string; description?: string }> {
    const model = this.model();
    if (!model) {
      throw new Error('Model is not available');
    }

    if (!this.canShowEvidenceContent(evidence)) {
      this._logger.error('Invalid evidence detected in getSensitiveDataMethod', {
        evidence,
        stepId: evidence?.stepId,
        hasCoreEvidence: this.hasCoreEvidence(evidence),
      });
      throw new Error('Invalid evidence: stepId or coreEvidence is missing');
    }

    return () => {
      return this._searchActivityHttpService.findInLineActivityStepByIdViaQuery(evidence.stepId).pipe(
        map((step) => ({
          transcript: step?.transcript || evidence.coreEvidence,
          resultCode: 'success',
          title: 'Core Evidence',
          description: 'Evidence content',
        }))
      );
    };
  }
}
